import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Alert, Box, Button, CircularProgress, Link, Typography,
} from '@mui/material';
import { authService } from '../../services/authService';
import { getHomeRoute } from '../../config/roles';
import FormProvider from '../../components/form/FormProvider';
import TextField from '../../components/form/TextField';
import PasswordField from '../../components/form/PasswordField';
import AuthBrandPanel from './components/AuthBrandPanel';
import {
  AUTH_RED,
  authFieldSx,
  authFormPanelSx,
  authLabelSx,
  authMobileLogoChipSx,
  authPrimaryButtonSx,
  authShellSx,
} from './components/authFormStyles';

const loginSchema = z.object({
  email: z.email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const methods = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const navigateByRole = (user) => {
    navigate(getHomeRoute(user.role));
  };

  // Handle external login callback: ?external_login=success or ?error=...
  useEffect(() => {
    const externalLogin = searchParams.get('external_login');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setError(decodeURIComponent(errorParam));
      setSearchParams({}, { replace: true });
      return;
    }

    if (externalLogin === 'success') {
      setLoading(true);
      setSearchParams({}, { replace: true });
      authService.exchangeExternalLogin()
        .then((result) => navigateByRole(result.user))
        .catch((err) => {
          setError(err.message || 'External login failed.');
          setLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSignIn = async ({ email, password }) => {
    setError('');
    setLoading(true);
    try {
      const result = await authService.login(email, password);
      navigateByRole(result.user);
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    const email = methods.getValues('email');
    const emailValid = loginSchema.shape.email.safeParse(email);
    const resetUrl = emailValid.success
      ? `/auth/reset-password?email=${encodeURIComponent(email)}`
      : '/auth/reset-password';
    navigate(resetUrl);
  };

  return (
    <Box sx={authShellSx}>
      <AuthBrandPanel />

      <Box sx={authFormPanelSx}>
        <Box sx={{ width: '100%', maxWidth: 400 }}>
          <Box sx={{ display: { xs: 'flex', lg: 'none' }, justifyContent: 'center', mb: 4 }}>
            <Box sx={authMobileLogoChipSx}>
              <Box component="img" src="/assets/pp-logo.png" alt="PRO Portal" sx={{ height: 36 }} />
            </Box>
          </Box>

          <Typography
            component="h2"
            sx={{ fontSize: 24, fontWeight: 800, color: 'inherit', letterSpacing: '-.02em' }}
          >
            Welcome back
          </Typography>
          <Typography sx={{ fontSize: 13.5, color: 'rgba(0,0,0,.5)', mb: '24px', mt: 0.5 }}>
            Sign in to your PRO Portal account
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert>}

          <FormProvider methods={methods} onSubmit={onSignIn} autoComplete="off">
            <Typography component="label" htmlFor="login-email" sx={authLabelSx}>
              Email
            </Typography>
            <TextField
              id="login-email"
              name="email"
              label=""
              type="email"
              disabled={loading}
              autoComplete="username"
              sx={{ ...authFieldSx, mb: 2.25 }}
              InputLabelProps={{ shrink: false }}
            />

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 0.75,
              }}
            >
              <Typography component="label" htmlFor="login-password" sx={{ ...authLabelSx, mb: 0 }}>
                Password
              </Typography>
              <Link
                component="button"
                type="button"
                underline="none"
                tabIndex={-1}
                onClick={handleForgotPassword}
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: AUTH_RED,
                  fontFamily: "'Inter', system-ui, sans-serif",
                  '&:hover': { color: '#b00303' },
                }}
              >
                Forgot password?
              </Link>
            </Box>
            <PasswordField
              id="login-password"
              name="password"
              label=""
              disabled={loading}
              autoComplete="current-password"
              sx={{ ...authFieldSx, mb: 2.5 }}
              InputLabelProps={{ shrink: false }}
            />

            <Button type="submit" fullWidth disabled={loading} sx={authPrimaryButtonSx}>
              {loading ? (
                <><CircularProgress size={18} color="inherit" sx={{ mr: 1 }} /> Signing in…</>
              ) : (
                'Sign in'
              )}
            </Button>
          </FormProvider>

          <Typography
            align="center"
            sx={{ mt: 3.5, fontSize: 12.5, color: 'rgba(0,0,0,.55)' }}
          >
            Are you a broker?{' '}
            <Link
              component={RouterLink}
              to="/signup"
              underline="none"
              sx={{ fontWeight: 700, color: AUTH_RED, '&:hover': { color: '#b00303' } }}
            >
              Apply to become a partner
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;
